// calendarApi.js - API-funktioner för kalendermodulen

import apiClient from '../../apiClient';

const BASE_URL = '/api/calendar';

// ============== USERS API (för behandlar-filter) ==============

export const getClinicUsers = async () => {
  const response = await apiClient.get('/api/users');
  return response.data;
};

// ============== PATIENT API ==============

export const getPatients = async (search = '', options = {}) => {
  const params = { search };
  if (options.user_id) params.user_id = options.user_id;
  if (options.limit) params.limit = options.limit;
  if (options.offset) params.offset = options.offset;
  if (options.sort_by) params.sort_by = options.sort_by;
  if (options.sort_order) params.sort_order = options.sort_order;
  const response = await apiClient.get(`${BASE_URL}/patients`, { params });
  return response.data;
};

export const getPatient = async (patientId) => {
  const response = await apiClient.get(`${BASE_URL}/patients/${patientId}`);
  return response.data;
};

export const createPatient = async (patientData) => {
  const response = await apiClient.post(`${BASE_URL}/patients`, patientData);
  return response.data;
};

export const updatePatient = async (patientId, patientData) => {
  const response = await apiClient.put(`${BASE_URL}/patients/${patientId}`, patientData);
  return response.data;
};

export const deletePatient = async (patientId) => {
  const response = await apiClient.delete(`${BASE_URL}/patients/${patientId}`);
  return response.data;
};

// ============== APPOINTMENT TYPE API ==============

export const getAppointmentTypes = async () => {
  const response = await apiClient.get(`${BASE_URL}/appointment-types`);
  return response.data;
};

export const createAppointmentType = async (typeData) => {
  const response = await apiClient.post(`${BASE_URL}/appointment-types`, typeData);
  return response.data;
};

export const updateAppointmentType = async (typeId, typeData) => {
  const response = await apiClient.put(`${BASE_URL}/appointment-types/${typeId}`, typeData);
  return response.data;
};

export const deleteAppointmentType = async (typeId) => {
  const response = await apiClient.delete(`${BASE_URL}/appointment-types/${typeId}`);
  return response.data;
};

// ============== APPOINTMENT API ==============

export const getAppointments = async (startDate, endDate, userId = null) => {
  const params = { start: startDate, end: endDate };
  if (userId) params.user_id = userId;
  const response = await apiClient.get(`${BASE_URL}/appointments`, { params });
  return response.data;
};

export const getAppointment = async (appointmentId) => {
  const response = await apiClient.get(`${BASE_URL}/appointments/${appointmentId}`);
  return response.data;
};

export const createAppointment = async (appointmentData) => {
  const response = await apiClient.post(`${BASE_URL}/appointments`, appointmentData);
  return response.data;
};

export const updateAppointment = async (appointmentId, appointmentData) => {
  const response = await apiClient.put(`${BASE_URL}/appointments/${appointmentId}`, appointmentData);
  return response.data;
};

export const deleteAppointment = async (appointmentId) => {
  const response = await apiClient.delete(`${BASE_URL}/appointments/${appointmentId}`);
  return response.data;
};

export const cancelAppointment = async (appointmentId) => {
  const response = await apiClient.post(`${BASE_URL}/appointments/${appointmentId}/cancel`);
  return response.data;
};

// ============== PAYMENT API ==============

export const registerPayment = async (paymentData) => {
  const response = await apiClient.post(`${BASE_URL}/payments`, paymentData);
  return response.data;
};

export const getPayment = async (paymentId) => {
  const response = await apiClient.get(`${BASE_URL}/payments/${paymentId}`);
  return response.data;
};

export const deletePayment = async (paymentId) => {
  const response = await apiClient.delete(`${BASE_URL}/payments/${paymentId}`);
  return response.data;
};

export const getDailyCashReport = async (startDate, endDate = null, userId = null) => {
  const params = {};
  if (endDate && startDate !== endDate) {
    // Datumintervall
    params.start_date = startDate;
    params.end_date = endDate;
  } else {
    // Enskilt datum (bakåtkompatibelt)
    params.date = startDate;
  }
  if (userId) params.user_id = userId;
  const response = await apiClient.get(`${BASE_URL}/daily-cash-report`, { params });
  return response.data;
};

export const getReceiptPdf = async (paymentId) => {
  const response = await apiClient.get(`${BASE_URL}/payments/${paymentId}/receipt`, {
    responseType: 'blob'
  });
  // Skapa download
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `kvitto_${paymentId}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// ============== BLOCK/NOTE API ==============

export const getBlocks = async (startDate, endDate, userId = null, blockType = null) => {
  const params = { start: startDate, end: endDate };
  if (userId) params.user_id = userId;
  if (blockType) params.type = blockType;
  const response = await apiClient.get(`${BASE_URL}/blocks`, { params });
  return response.data;
};

export const getBlock = async (blockId) => {
  const response = await apiClient.get(`${BASE_URL}/blocks/${blockId}`);
  return response.data;
};

export const createBlock = async (blockData) => {
  const response = await apiClient.post(`${BASE_URL}/blocks`, blockData);
  return response.data;
};

export const updateBlock = async (blockId, blockData) => {
  const response = await apiClient.put(`${BASE_URL}/blocks/${blockId}`, blockData);
  return response.data;
};

export const deleteBlock = async (blockId, deleteSeries = false) => {
  const params = deleteSeries ? { delete_series: 'true' } : {};
  const response = await apiClient.delete(`${BASE_URL}/blocks/${blockId}`, { params });
  return response.data;
};

export const checkAvailability = async (startTime, endTime, userId = null) => {
  const response = await apiClient.post(`${BASE_URL}/check-availability`, {
    start_time: startTime,
    end_time: endTime,
    user_id: userId
  });
  return response.data;
};

// ============== GROUP APPOINTMENT API ==============

export const createGroupAppointment = async (groupData) => {
  const response = await apiClient.post(`${BASE_URL}/group-appointments`, groupData);
  return response.data;
};

export const getGroupAppointment = async (appointmentId) => {
  const response = await apiClient.get(`${BASE_URL}/group-appointments/${appointmentId}`);
  return response.data;
};

export const getGroupParticipants = async (appointmentId) => {
  const response = await apiClient.get(`${BASE_URL}/group-appointments/${appointmentId}/participants`);
  return response.data;
};

export const addGroupParticipant = async (appointmentId, patientId, notes = null) => {
  const response = await apiClient.post(`${BASE_URL}/group-appointments/${appointmentId}/participants`, {
    patient_id: patientId,
    notes
  });
  return response.data;
};

export const removeGroupParticipant = async (appointmentId, patientId) => {
  const response = await apiClient.delete(`${BASE_URL}/group-appointments/${appointmentId}/participants/${patientId}`);
  return response.data;
};

export const updateParticipantStatus = async (appointmentId, patientId, status, notes = null) => {
  const response = await apiClient.put(`${BASE_URL}/group-appointments/${appointmentId}/participants/${patientId}/status`, {
    status,
    notes
  });
  return response.data;
};

// ============== GROUP TEMPLATES & WAITLIST API ==============

export const getGroupTemplates = async () => {
  const response = await apiClient.get(`${BASE_URL}/groups/templates`);
  return response.data;
};

export const createGroupTemplate = async (templateData) => {
  const response = await apiClient.post(`${BASE_URL}/groups/templates`, templateData);
  return response.data;
};

export const getGroupTemplate = async (templateId) => {
  const response = await apiClient.get(`${BASE_URL}/groups/templates/${templateId}`);
  return response.data;
};

export const updateGroupTemplate = async (templateId, templateData) => {
  const response = await apiClient.put(`${BASE_URL}/groups/templates/${templateId}`, templateData);
  return response.data;
};

export const deleteGroupTemplate = async (templateId) => {
  const response = await apiClient.delete(`${BASE_URL}/groups/templates/${templateId}`);
  return response.data;
};

export const getWaitlist = async (templateId) => {
  const response = await apiClient.get(`${BASE_URL}/groups/templates/${templateId}/waitlist`);
  return response.data;
};

export const addToWaitlist = async (templateId, patientId, notes = null) => {
  const response = await apiClient.post(`${BASE_URL}/groups/templates/${templateId}/waitlist`, {
    patient_id: patientId,
    notes
  });
  return response.data;
};

export const removeFromWaitlist = async (templateId, waitlistId) => {
  const response = await apiClient.delete(`${BASE_URL}/groups/templates/${templateId}/waitlist/${waitlistId}`);
  return response.data;
};

export const bookFromWaitlist = async (templateId, appointmentId, waitlistIds = null) => {
  const response = await apiClient.post(`${BASE_URL}/groups/templates/${templateId}/book-from-waitlist`, {
    appointment_id: appointmentId,
    waitlist_ids: waitlistIds
  });
  return response.data;
};

// ============== GROUP PARTICIPANTS WITH PAYMENTS ==============

export const getParticipantsWithPayments = async (appointmentId) => {
  const response = await apiClient.get(`${BASE_URL}/groups/appointments/${appointmentId}/participants-with-payments`);
  return response.data;
};

export const registerParticipantPayment = async (appointmentId, patientId, paymentData) => {
  const response = await apiClient.post(`${BASE_URL}/groups/appointments/${appointmentId}/register-participant-payment`, {
    patient_id: patientId,
    ...paymentData
  });
  return response.data;
};

// ============== SCHEDULE API ==============

export const getUserSchedules = async (userId = null) => {
  const params = userId ? { user_id: userId } : {};
  const response = await apiClient.get(`${BASE_URL}/schedules`, { params });
  return response.data;
};

export const getUserSchedule = async (userId) => {
  const response = await apiClient.get(`${BASE_URL}/schedules/${userId}`);
  return response.data;
};

export const updateUserSchedule = async (userId, schedule, validFrom = null) => {
  const data = { schedule };
  if (validFrom) {
    data.valid_from = validFrom;
  }
  const response = await apiClient.put(`${BASE_URL}/schedules/${userId}`, data);
  return response.data;
};

export const deleteUserSchedule = async (userId) => {
  const response = await apiClient.delete(`${BASE_URL}/schedules/${userId}`);
  return response.data;
};

export const checkSchedule = async (userId, startTime, endTime = null) => {
  const response = await apiClient.post(`${BASE_URL}/check-schedule`, {
    user_id: userId,
    start_time: startTime,
    end_time: endTime
  });
  return response.data;
};

// ============== AVAILABLE SLOTS API ==============

export const getAvailableSlots = async (userId, appointmentTypeId, options = {}) => {
  const params = {
    user_id: userId,
    appointment_type_id: appointmentTypeId
  };
  if (options.from_date) params.from_date = options.from_date;
  if (options.to_date) params.to_date = options.to_date;
  if (options.limit) params.limit = options.limit;
  const response = await apiClient.get(`${BASE_URL}/available-slots`, { params });
  return response.data;
};

// ============== SCHEDULE BLOCKS API (för kalendervy) ==============

export const getScheduleBlocks = async (userId, fromDate, toDate) => {
  const params = {
    user_id: userId,
    from_date: fromDate,
    to_date: toDate
  };
  const response = await apiClient.get(`${BASE_URL}/schedule-blocks`, { params });
  return response.data;
};

// ============== PRODUCT API ==============

export const getProducts = async (activeOnly = true) => {
  const params = { active_only: activeOnly };
  const response = await apiClient.get(`${BASE_URL}/products`, { params });
  return response.data;
};

export const getProduct = async (productId) => {
  const response = await apiClient.get(`${BASE_URL}/products/${productId}`);
  return response.data;
};

export const createProduct = async (productData) => {
  const response = await apiClient.post(`${BASE_URL}/products`, productData);
  return response.data;
};

export const updateProduct = async (productId, productData) => {
  const response = await apiClient.put(`${BASE_URL}/products/${productId}`, productData);
  return response.data;
};

export const deleteProduct = async (productId) => {
  const response = await apiClient.delete(`${BASE_URL}/products/${productId}`);
  return response.data;
};

// ============== SALE/POS API ==============

export const createSale = async (saleData) => {
  const response = await apiClient.post(`${BASE_URL}/sales`, saleData);
  return response.data;
};

export const getSale = async (saleId) => {
  const response = await apiClient.get(`${BASE_URL}/sales/${saleId}`);
  return response.data;
};

export const getPatientSales = async (patientId) => {
  const response = await apiClient.get(`${BASE_URL}/patients/${patientId}/sales`);
  return response.data.sales || [];
};

export const getPatientPayments = async (patientId) => {
  const response = await apiClient.get(`${BASE_URL}/patients/${patientId}/payments`);
  return response.data;
};

export const getSaleReceiptPdf = async (saleId) => {
  const response = await apiClient.get(`${BASE_URL}/sales/${saleId}/receipt`, {
    responseType: 'blob'
  });
  // Skapa download
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `kvitto_sale_${saleId}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// ============== PRODUCT STATISTICS API ==============

export const getProductStatistics = async (fromDate = null, toDate = null, productId = null) => {
  const params = new URLSearchParams();
  if (fromDate) params.append('from_date', fromDate);
  if (toDate) params.append('to_date', toDate);
  if (productId) params.append('product_id', productId);
  const response = await apiClient.get(`${BASE_URL}/product-statistics?${params.toString()}`);
  return response.data;
};

export const exportDailyCashReport = async (startDate, endDate = null, userId = null, format = 'csv') => {
  const params = new URLSearchParams();
  if (endDate && startDate !== endDate) {
    params.append('start_date', startDate);
    params.append('end_date', endDate);
  } else {
    params.append('date', startDate);
  }
  if (userId) params.append('user_id', userId);
  params.append('format', format);

  const response = await apiClient.get(`${BASE_URL}/daily-cash-report/export?${params.toString()}`, {
    responseType: 'blob'
  });

  // Skapa download - filnamn hanteras av Content-Disposition från servern
  const contentDisposition = response.headers['content-disposition'];
  let filename = startDate === endDate || !endDate
    ? `dagskassa_${startDate}.csv`
    : `kassarapport_${startDate}_till_${endDate}.csv`;

  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename=(.+)/);
    if (filenameMatch) filename = filenameMatch[1];
  }

  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const emailReceipt = async (paymentId, email = null) => {
  const response = await apiClient.post(`${BASE_URL}/payments/${paymentId}/email-receipt`, {
    email: email
  });
  return response.data;
};

// ============== KALLELSE API ==============

export const getKallelseTemplates = async () => {
  const response = await apiClient.get('/api/kallelsemallar');
  return response.data;
};

export const sendKallelse = async (data) => {
  const response = await apiClient.post('/api/kallelsemallar/send', data);
  return response.data;
};

// ============== SCHEMAMALLAR API ==============

export const getScheduleTemplates = async () => {
  const response = await apiClient.get(`${BASE_URL}/schedule-templates`);
  return response.data;
};

export const getScheduleTemplate = async (templateId) => {
  const response = await apiClient.get(`${BASE_URL}/schedule-templates/${templateId}`);
  return response.data;
};

export const createScheduleTemplate = async (templateData) => {
  const response = await apiClient.post(`${BASE_URL}/schedule-templates`, templateData);
  return response.data;
};

export const updateScheduleTemplate = async (templateId, templateData) => {
  const response = await apiClient.put(`${BASE_URL}/schedule-templates/${templateId}`, templateData);
  return response.data;
};

export const deleteScheduleTemplate = async (templateId) => {
  const response = await apiClient.delete(`${BASE_URL}/schedule-templates/${templateId}`);
  return response.data;
};

// ============== SCHEMATILLDELNINGAR API ==============

export const getScheduleAssignments = async (userId = null) => {
  const params = {};
  if (userId) params.user_id = userId;
  const response = await apiClient.get(`${BASE_URL}/schedule-assignments`, { params });
  return response.data;
};

export const createScheduleAssignment = async (assignmentData) => {
  const response = await apiClient.post(`${BASE_URL}/schedule-assignments`, assignmentData);
  return response.data;
};

export const updateScheduleAssignment = async (assignmentId, assignmentData) => {
  const response = await apiClient.put(`${BASE_URL}/schedule-assignments/${assignmentId}`, assignmentData);
  return response.data;
};

export const deleteScheduleAssignment = async (assignmentId) => {
  const response = await apiClient.delete(`${BASE_URL}/schedule-assignments/${assignmentId}`);
  return response.data;
};

// ============== EFFEKTIVA SLOTS + AVVIKELSER API ==============

export const getEffectiveSlots = async (userId, { date, from, to } = {}) => {
  const params = { user_id: userId };
  if (date) params.date = date;
  if (from) params.from = from;
  if (to) params.to = to;
  const response = await apiClient.get(`${BASE_URL}/effective-slots`, { params });
  return response.data;
};

export const createSlotOverride = async (overrideData) => {
  const response = await apiClient.post(`${BASE_URL}/slot-overrides`, overrideData);
  return response.data;
};

export const deleteSlotOverride = async (overrideId) => {
  const response = await apiClient.delete(`${BASE_URL}/slot-overrides/${overrideId}`);
  return response.data;
};
